import type { App } from 'vue';
import type { AxiosRequestConfig } from 'axios';
import { useI18n } from '@/app/modules/i18n';
import { Transport } from './transport';
import { Auth } from './auth';
import type { AuthState, IApiClient, WithConfig, TransportLike } from './types';
import { API_SYMBOL } from './shared';
const optionKeys = new Set([
  'baseURL',
  'data',
  'params',
  'headers',
  'responseType',
  'signal',
  'onUploadProgress',
  'onDownloadProgress',
]);
function isTransportOptions(x: unknown): x is AxiosRequestConfig {
  if (
    typeof x !== 'object' ||
    x == null ||
    Array.isArray(x) ||
    Object.prototype.toString.call(x) !== '[object Object]'
  ) {
    return false;
  }
  const keys = Object.keys(x);
  if (keys.length === 0) {
    return false;
  }
  if (!keys.every((k) => optionKeys.has(k))) {
    return false;
  }
  return true;
}
function withOptionsProxy(transport: Transport, base: AxiosRequestConfig): TransportLike {
  return {
    get(url, options) {
      return transport.get(url, { ...base, ...(options ?? {}) });
    },
    post(url, data, options) {
      return transport.post(url, data, { ...base, ...(options ?? {}) });
    },
    put(url, data, options) {
      return transport.put(url, data, { ...base, ...(options ?? {}) });
    },
    patch(url, data, options) {
      return transport.patch(url, data, { ...base, ...(options ?? {}) });
    },
    delete(url, options) {
      return transport.delete(url, { ...base, ...(options ?? {}) });
    },
  };
}
export interface ApiClientOptions {
  baseUrl: string;
  keycloak: {
    url: string;
    realm: string;
    clientId: string;
    silentCheckSsoRedirectUri: string;
    logoutRedirectUri: string;
  };
}
export class ApiClient implements IApiClient {
  public transport: Transport;
  public auth: Auth;
  constructor(private options: ApiClientOptions) {
    const transport = new Transport({ baseUrl: options.baseUrl });
    const auth = new Auth({ ...options.keycloak, transport });
    transport.setAccessTokenGetter(() => auth.getToken());
    transport.setAuthErrorHandler(() => auth.logout());
    this.transport = transport;
    this.auth = auth;
    this.createMethod = this.createMethod.bind(this);
  }
  init() {
    return this.auth.init();
  }
  install(app: App) {
    app.provide(API_SYMBOL, this);
    this.transport.setLocaleGetter(() => useI18n().locale.value);
  }
  onAuthStateChange(handler: (state: AuthState) => void): () => void {
    return this.auth.onStateChanged(handler);
  }
  createMethod<T extends any[], R = unknown>(
    fn: (transport: TransportLike, ...args: T) => Promise<R>,
  ) {
    return (...args: WithConfig<T>) => {
      const lastArgument = args[args.length - 1];
      const hasOptions = isTransportOptions(lastArgument);
      const options = hasOptions ? (lastArgument as AxiosRequestConfig) : undefined;
      const pureArgs = (hasOptions ? args.slice(0, -1) : args) as T;
      const transportForCall: TransportLike = options
        ? withOptionsProxy(this.transport, options)
        : this.transport;
      return fn(transportForCall, ...pureArgs);
    };
  }
}
